export class Callout {
    id: number;
    mapPoint: __esri.Point;
    boxAlignment: string = 'Top Left';
    tailSize: number = 20;
    boxWidth: number = 200;
    boxHeight: number = 100;
    cornerRadius: number = 15;
    horizontalPadding: number = 15;
    graphicsLayer: NonNullable<__esri.GraphicsLayer>;
    calloutGraphic: NonNullable<__esri.Graphic>;
    textGraphic: NonNullable<__esri.Graphic>;

    boxWidthMin = 50;
    boxWidthMax = 600;
    boxHeightMin = 50;
    boxHeightMax = 600;
    tailSizeMin = 0;
    tailSizeMax = 100;
    maxPrecision = 2;
    tailXOffset = 2;
    tailyOffsetDivisor = 6;
    scaleDivisor = 3779.5200000002;

    symbolProperties: __esri.SimpleFillSymbolProperties = {
        type: 'simple-fill',
        color: 'white',
        style: 'solid',
        outline: {
            color: 'black',
            style: 'solid',
            width: 2
        }
    };

    textProperties: __esri.TextSymbolProperties = {
        text: 'My text',
        color: 'black',
        horizontalAlignment: 'center',
        verticalAlignment: 'middle',
        font: {
            size: 20,
            family: 'Arial',
            weight: 'bold'
        }
    };

    pictureMarkerSymbolProperties: __esri.PictureMarkerSymbolProperties = {
        type: 'picture-marker',
        url: null,
        height: 200,
        width: 200
    };

    constructor(mapPoint: __esri.Point) {
        this.id = Date.now();
        this.mapPoint = mapPoint;
    }

    getGeometry(currentScale: number) {
        const scalar = currentScale / this.scaleDivisor;
        const bw = this.boxWidth * scalar;
        const bh = this.boxHeight * scalar;
        const th = this.tailSize * scalar;
        const tw = Math.floor(th * 0.6);
        const cr = this.cornerRadius * scalar;

        const ring: Vertex[] = [];

        const draw = (change: Vertex) => {
            ring.push(change);
            return change;
        };

        const drawMany = (changes: Vertex[]) => {
            ring.push(...changes);
            return changes[changes.length - 1];
        };

        const curve = (startPoint: Vertex, controlPoint: Vertex, endPoint: Vertex, totalPoints: number) => {
            const curvePoints: Vertex[] = [];
            for (let i = 0; i < totalPoints; i++) {
                const t = i / totalPoints;
                curvePoints.push([
                    (1 - t) * (1 - t) * startPoint[0] + 2 * (1 - t) * t * controlPoint[0] + t * t * endPoint[0],
                    (1 - t) * (1 - t) * startPoint[1] + 2 * (1 - t) * t * controlPoint[1] + t * t * endPoint[1]
                ]);
            }
            return curvePoints;
        };

        const curveTopLeft = (c: Vertex) => drawMany(curve(c, [c[0] - cr, c[1]], [c[0] - cr, c[1] - cr], this.cornerRadius));
        const curveTopRight = (c: Vertex) => drawMany(curve(c, [c[0], c[1] + cr], [c[0] - cr, c[1] + cr], this.cornerRadius));
        const curveBottomLeft = (c: Vertex) => drawMany(curve(c, [c[0], c[1] - cr], [c[0] + cr, c[1] - cr], this.cornerRadius));
        const curveBottomRight = (c: Vertex) => drawMany(curve(c, [c[0] + cr, c[1]], [c[0] + cr, c[1] + cr], this.cornerRadius));

        let cursor = draw([this.mapPoint.x, this.mapPoint.y]);
        let tailEnd: Vertex;
        switch (this.boxAlignment) {
            case 'Top Left':
                tailEnd = cursor = draw([cursor[0], cursor[1] - th]);
                cursor = draw([cursor[0] - ((bw / 4) - cr), cursor[1]]);
                cursor = curveTopLeft(cursor);
                cursor = draw([cursor[0], cursor[1] - (bh - cr * 2)]);
                cursor = curveBottomLeft(cursor);
                cursor = draw([cursor[0] + (bw - (cr * 2)), cursor[1]]);
                cursor = curveBottomRight(cursor);
                cursor = draw([cursor[0], cursor[1] + (bh - cr * 2)]);
                cursor = curveTopRight(cursor);
                cursor = draw([tailEnd[0] + tw, tailEnd[1]]);
                break;
            case 'Bottom Left':
                tailEnd = cursor = draw([cursor[0] + tw, cursor[1] + th]);
                cursor = draw([cursor[0] + (((bw / 4) * 3) - cr - tw), cursor[1]]);
                cursor = curveBottomRight(cursor);
                cursor = draw([cursor[0], cursor[1] + (bh - cr * 2)]);
                cursor = curveTopRight(cursor);
                cursor = draw([cursor[0] - (bw - (cr * 2)), cursor[1]]);
                cursor = curveTopLeft(cursor);
                cursor = draw([cursor[0], cursor[1] - (bh - cr * 2)]);
                cursor = curveBottomLeft(cursor);
                cursor = draw([tailEnd[0] - tw, tailEnd[1]]);
                break;
            case 'Top Right':
                tailEnd = cursor = draw([cursor[0] - tw, cursor[1] - th]);
                cursor = draw([cursor[0] - (((bw / 4) * 3) - cr - tw), cursor[1]]);
                cursor = curveTopLeft(cursor);
                cursor = draw([cursor[0], cursor[1] - (bh - cr * 2)]);
                cursor = curveBottomLeft(cursor);
                cursor = draw([cursor[0] + (bw - (cr * 2)), cursor[1]]);
                cursor = curveBottomRight(cursor);
                cursor = draw([cursor[0], cursor[1] + (bh - cr * 2)]);
                cursor = curveTopRight(cursor);
                cursor = draw([tailEnd[0] + tw, tailEnd[1]]);
                break;
            default:
                tailEnd = cursor = draw([cursor[0], cursor[1] + th]);
                cursor = draw([cursor[0] + ((bw / 4) - cr), cursor[1]]);
                cursor = curveBottomRight(cursor);
                cursor = draw([cursor[0], cursor[1] + (bh - cr * 2)]);
                cursor = curveTopRight(cursor);
                cursor = draw([cursor[0] - (bw - (cr * 2)), cursor[1]]);
                cursor = curveTopLeft(cursor);
                cursor = draw([cursor[0], cursor[1] - (bh - cr * 2)]);
                cursor = curveBottomLeft(cursor);
                cursor = draw([tailEnd[0] - tw, tailEnd[1]]);
                break;
        }
        cursor = draw([this.mapPoint.x, this.mapPoint.y]);

        return ring;
    }

    setGeometry(currentScale: number) {
        const scalar = currentScale / this.scaleDivisor;
        const currentRing = (<__esri.Polygon>this.calloutGraphic.geometry).rings[0];
        const extent = (<__esri.Polygon>this.calloutGraphic.geometry).extent;
        const th = Math.abs(currentRing[0][1] - currentRing[1][1]);
        this.tailSize = th / scalar;
        this.boxHeight = (extent.height - th) / scalar;
        this.boxWidth = extent.width / scalar;
        this.mapPoint.x = currentRing[0][0];
        this.mapPoint.y = currentRing[0][1];

        this.normalizeMinMax();
        this.calloutGraphic.attributes.tailSize = this.tailSize;
        this.calloutGraphic.attributes.boxHeight = this.boxHeight;
        this.calloutGraphic.attributes.boxWidth = this.boxWidth;
    }

    normalizeMinMax() {
        if (this.tailSize > this.tailSizeMax) {
            this.tailSize = this.tailSizeMax;
        }
        if (this.tailSize < this.tailSizeMin) {
            this.tailSize = this.tailSizeMin;
        }
        if (this.boxHeight > this.boxHeightMax) {
            this.boxHeight = this.boxHeightMax;
        }
        if (this.boxHeight < this.boxHeightMin) {
            this.boxHeight = this.boxHeightMin;
        }
        if (this.boxWidth > this.boxWidthMax) {
            this.boxWidth = this.boxWidthMax;
        }
        if (this.boxWidth < this.boxWidthMin) {
            this.boxWidth = this.boxWidthMin;
        }
        this.tailSize = parseFloat(this.tailSize.toPrecision(this.maxPrecision));
        this.boxHeight = parseFloat(this.boxHeight.toPrecision(this.maxPrecision));
        this.boxWidth = parseFloat(this.boxWidth.toPrecision(this.maxPrecision));
    }

    updateGeometry(currentScale: number) {
        this.calloutGraphic = this.graphicsLayer.graphics.find(graphic => graphic.geometry.type === 'polygon');
        this.boxAlignment = this.calloutGraphic.attributes.boxAlignment;
        this.tailSize = this.calloutGraphic.attributes.tailSize;
        this.boxWidth = this.calloutGraphic.attributes.boxWidth;
        this.boxHeight = this.calloutGraphic.attributes.boxHeight;
        this.cornerRadius = this.calloutGraphic.attributes.cornerRadius;
        this.id = this.calloutGraphic.attributes.id;
        const updatedGeometry = <__esri.Polygon>this.calloutGraphic.geometry.clone();
        updatedGeometry.rings = [this.getGeometry(currentScale)];
        this.calloutGraphic.geometry = updatedGeometry;
        this.textGraphic = this.graphicsLayer.graphics.find(graphic => graphic.geometry.type === 'point');
        this.textGraphic.geometry = updatedGeometry.centroid.clone();
    }

    getTextOffset() {
        const xMod = this.boxAlignment.includes('Right') ? -1 : 1;
        const yMod = this.boxAlignment.includes('Top') ? -1 : 1;

        return {
            x: `${xMod * this.tailXOffset}px`,
            y: `${yMod * (this.tailSize / this.tailyOffsetDivisor)}px`
        };
    }

    /**
     * Returns a Callout from a known Callout layer
     */
    static fromLayer(layer: __esri.GraphicsLayer) {
        const retVal = new Callout(null);
        retVal.graphicsLayer = layer;

        retVal.calloutGraphic = layer.graphics.find(graphic => graphic.attributes.part === 'box');
        retVal.id = retVal.calloutGraphic.attributes.id;
        retVal.boxAlignment = retVal.calloutGraphic.attributes.boxAlignment;
        retVal.tailSize = retVal.calloutGraphic.attributes.tailSize;
        retVal.boxWidth = retVal.calloutGraphic.attributes.boxWidth;
        retVal.boxHeight = retVal.calloutGraphic.attributes.boxHeight;
        retVal.cornerRadius = retVal.calloutGraphic.attributes.cornerRadius;
        retVal.mapPoint = (<__esri.Polygon>retVal.calloutGraphic.geometry).getPoint(0, 0);
        const fillSymbol = retVal.calloutGraphic.symbol as __esri.SimpleFillSymbolProperties;
        retVal.symbolProperties.color = (<__esri.Color>fillSymbol.color).toCss(true);
        retVal.symbolProperties.outline.color = (<__esri.Color>fillSymbol.outline.color).toCss(true);
        retVal.symbolProperties.outline.width = fillSymbol.outline.width;

        retVal.textGraphic = layer.graphics.find(graphic => graphic.attributes.part === 'text');
        if (!retVal.textGraphic) {
            retVal.textGraphic = layer.graphics.find(graphic => graphic.attributes.part === 'image');
            retVal.textGraphic.attributes.part = 'image';
            const pictureMarkerSymbol = retVal.textGraphic.symbol as __esri.PictureMarkerSymbolProperties;
            retVal.pictureMarkerSymbolProperties.height = (pictureMarkerSymbol.url as any).height;
            retVal.pictureMarkerSymbolProperties.width = (pictureMarkerSymbol.url as any).width;
            retVal.pictureMarkerSymbolProperties.url = (pictureMarkerSymbol.url as any).imageData ? (pictureMarkerSymbol.url as any).imageData : pictureMarkerSymbol.url;
            if (!retVal.pictureMarkerSymbolProperties.url.startsWith('data:image/png;base64,')) {
                retVal.pictureMarkerSymbolProperties.url = 'data:image/png;base64,' + retVal.pictureMarkerSymbolProperties.url;
            }
        } else {
            const textSymbol = retVal.textGraphic.symbol as __esri.TextSymbolProperties;
            retVal.textProperties.text = textSymbol.text;
            retVal.textProperties.color = (<__esri.Color>textSymbol.color).toCss(true);
            retVal.textProperties.font.size = textSymbol.font.size;
        }

        return retVal;
    }

    /**
     * Returns an array of Callouts from an array of possible Callout layers
     */
    static fromLayers(layers: __esri.GraphicsLayer[]) {
        return layers.filter(layer => layer.graphics.filter(graphic => graphic.attributes && graphic.attributes.type === 'callout').length).map(Callout.fromLayer.bind(this)) as Callout[];
    }
}

type Vertex = [number, number];
